/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import _ from 'lodash';
import urlJoin from 'proper-url-join';

import { endpoints } from '../../../ia/util/config';

export interface UpdateResponse {
  updated: string;
}

const transactionsEndpoint = (
  skill_id: string,
  useNewIAUpdatedAccess: boolean,
  assistant_id: string
): string =>
  //NOTE cannot use getAccessPrefixedUrl because 'tooling' location in url
  urlJoin(
    endpoints.store,
    useNewIAUpdatedAccess
      ? `/v2/tooling/assistants/${assistant_id}/skills/${skill_id}/transactions`
      : `/v2/tooling/skills/${skill_id}/transactions`
  );

/**
 * Create or update multiple counter examples
 * @param {string} skillId — The target skill id
 * @param {object} payload — The payload to be sent to the transaction api. Api expects payload containing an array
 * of operations. Each operation must have a type and an operation property (e.g. type: 'intent', operation: 'delete')
 * @returns {axios} An axios post call that returns an empty object
 */
export const sendTransactions = (
  skillId: string,
  payload: any,
  useNewIAUpdatedAccess: boolean, // = false,
  assistantId: string = ''
): Promise<UpdateResponse> => {
  if (!payload || !Array.isArray(payload.operations)) {
    return Promise.reject({
      error: 'Array of operations must be provided'
    });
  }
  if (
    payload.operations.some(
      operation => !operation.type || !operation.operation
    )
  ) {
    return Promise.reject({
      error:
        'Each operation in a single transaction must contain a type and an operation'
    });
  }
  const url = transactionsEndpoint(skillId, useNewIAUpdatedAccess, assistantId);
  return axios
    .post(url, payload)
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};
